import {
  PointOfInterest,
  PointOfInterestTemplate,
  useUpdatePointOfInterestMutation,
} from '@/__generated__/graphql';
import { DEFAULT_REVIEW_STATUS } from '@/constants';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useMemo } from 'react';
import { PillMenu } from '@/components/shared/PillMenu';
import { PillStatus } from '@/components/shared/Pill/types';

const getPillStatus = (status: string): PillStatus => {
  switch (status?.trim()) {
    case 'Pending review':
      return 'info';
    case 'Accept':
      return 'success';
    case 'False positive':
      return 'failed';
    default:
      return 'info';
  }
};

export const ReviewStatus = ({
  pointOfInterest,
  templateOfSelectedPoi,
  location = 'review-panel-poi',
}: {
  pointOfInterest: PointOfInterest;
  templateOfSelectedPoi: Partial<PointOfInterestTemplate> | undefined;
  location?: string;
}) => {
  const [updateMutation, { loading }] = useUpdatePointOfInterestMutation();
  const { showSnackBar } = useSnackBarMessage({});

  const statusField = useMemo(() => {
    if (!templateOfSelectedPoi?.fields) return null;
    return templateOfSelectedPoi?.fields?.find((field) => field.key === 'internal_review_status');
  }, [templateOfSelectedPoi?.fields]);

  const status = useMemo(() => {
    if (!statusField) return DEFAULT_REVIEW_STATUS;
    const stored = pointOfInterest.templateFieldData?.internal_review_status?.trim();
    if (stored) return stored;
    const firstOption = statusField.options?.[0];
    return (typeof firstOption === 'string' ? firstOption : undefined) ?? DEFAULT_REVIEW_STATUS;
  }, [pointOfInterest.templateFieldData, statusField]);

  if (!statusField) return <></>;

  const handleChange = async (nextStatus: string) => {
    const initialTemplateFieldData = (templateOfSelectedPoi?.fields ?? []).reduce((acc, field) => {
      if (field.key) {
        acc[field.key] = pointOfInterest.templateFieldData?.[field.key] ?? '';
      }
      return acc;
    }, {} as Record<string, string>);

    const templateFieldData: Record<string, string> = {
      ...initialTemplateFieldData,
      internal_review_status: nextStatus.trim(),
    };

    try {
      await updateMutation({
        variables: {
          input: {
            pointOfInterestId: pointOfInterest.id,
            templateId: templateOfSelectedPoi?.id ?? '',
            templateFieldData,
            lastModifiedFor: 'Update Review Status',
          },
        },
        optimisticResponse: {
          updatePointOfInterest: {
            __typename: 'PointOfInterest',
            id: pointOfInterest.id,
          },
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({ __typename: 'PointOfInterest', id: pointOfInterest.id }),
            fields: {
              templateFieldData: () => templateFieldData,
            },
          });
        },
      });
      showSnackBar('Review status updated successfully', 'success');
    } catch {
      showSnackBar('Failed to update review status. Please contact support', 'error');
    }
  };

  return (
    <PillMenu
      key={`${pointOfInterest.id}`}
      options={statusField.options}
      initialValue={status}
      loading={loading}
      loadingLabel="Updating..."
      onChange={handleChange}
      getPillStatus={getPillStatus}
      className={`${location}-status-pill-menu`}
    />
  );
};
